// What the operator does to a saved campaign: pause, resume, launch, publish,
// duplicate, extend, archive, delete, rename, disarm.

import { campaignPublishingFailure } from '@/lib/campaign-publish-failure';
import { campaignPublishOutcome, isLaunchRevisionConflict, LAUNCH_REVISION_CHANGED_MESSAGE } from '@/api/manage-hooks';
import { campaignSyncStatus } from '@/api/resources/manage';
import { networkOnlyRead } from '@/api/client';
import { reportActionError } from '@/lib/report-action-error';
import { deliveryTargetFor } from '@/lib/campaign-publish-sequence';
import { campaignIdForCreate } from '@/lib/campaign-plan';
import type { CampaignFlightExtension, CampaignPlan, CampaignStatus } from '@/api/types';
import { fmtDay } from '@/lib/datetime';
import type { CampaignPreflight } from '@/api/manage-types';
import type { CreateFlowState } from './create-flow-state';

export function campaignFlowActions(flow: CreateFlowState) {
  const { campaign, setView, navigate, queryClient, status, setStatus, channel, name, savedName,
    setSavedName, patchCampaign, preflightResult, setPreflightResult, setActionError,
    setActionNotice, duplicateCampaign, retryCampaignSync, archiveCampaign, deleteCampaign,
    launchCampaign, launchWhenReady, cancelLaunchWhenReady, pauseCampaign, resumeCampaign, 
    savedCampaignId, setMoreOpen, undoToast, setUndoToast, setLaunchOpen, publishProgress,
    setPublishProgress, audienceSyncGate, setAudienceSyncGate, creativeSyncGate,
    setCreativeSyncGate, freshCampaignSyncStatus, operationalStatus, setExtendOpen,
    applyPlanTerms, currentRequestedStatus, savedPlan,
  } = flow;

  // The plan revision the operator has reviewed: the one the form was seeded from, or the
  // one this wizard's own last save wrote. `savedPlan` holds exactly that and nothing else:
  // a save whose answer is any other revision is refused (`ownWriteOrChangedElsewhere`), and
  // the route's `campaign` prop is deliberately not consulted, because it follows the plan
  // query, which a refetch moves past what the form shows. A launch or an arm names it, so a
  // draft edited since (an API key's edit) is refused, never launched unseen.
  const reviewedRevision = (campaignId: string): number | null =>
    savedPlan?.campaign?.id === campaignId ? savedPlan.revision : null;

  const persistStatus = async (next: CampaignStatus) => {
    if (!campaign?.id) return;
    if (next === 'paused') await pauseCampaign.mutateAsync(campaign.id);
    else if (next === 'live') await resumeCampaign.mutateAsync(campaign.id);
  };

  const togglePauseResume = async (next: 'live' | 'paused') => {
    const prev = operationalStatus?.requested_state ?? status;
    setActionError(null);
    setStatus(next);
    try {
      await persistStatus(next);
      setUndoToast({ from: prev, to: next, key: Date.now() });
    } catch (error) {
      setStatus(prev);
      setActionError(reportActionError(error, { operation: 'campaign.toggle_pause' }));
    }
  };

  const undoStatus = async () => {
    if (!undoToast) return;
    const current = status;
    setStatus(undoToast.from);
    setUndoToast(null);
    try {
      await persistStatus(undoToast.from);
    } catch (error) {
      setStatus(current);
      setActionError(reportActionError(error, { operation: 'campaign.undo_status' }));
    }
  };

  const handleAudienceSyncComplete = async () => {
    const gate = audienceSyncGate;
    const campaignId = gate?.campaignId;
    if (!campaignId) return;
    setAudienceSyncGate(null);
    setActionError(null);
    setActionNotice(
      'Audience sync completed. Waiting for every selected creative to finish publishing.',
    );
    setCreativeSyncGate({
      campaignId,
      items: gate.creativeItems,
    });
  };

  const handleCreativeSyncComplete = async () => {
    const campaignId = creativeSyncGate?.campaignId;
    if (!campaignId) return;
    setCreativeSyncGate(null);
    setActionError(null);
    setActionNotice('Creative publishing completed. Running a fresh pre-flight check.');
    try {
      const check = await networkOnlyRead<CampaignPreflight>(
        `/campaigns/${encodeURIComponent(campaignId)}/preflight`,
      );
      setPreflightResult(check);
      if (check.ready) {
        setActionNotice(null);
        setLaunchOpen(true);
      } else {
        setActionNotice(
          'Audience and creative sync completed. Resolve the remaining failed pre-flight checks before marking this campaign ready to publish.',
        );
      }
    } catch (error) {
      setActionError(reportActionError(error, {
        operation: 'campaign.preflight_recheck',
        context: { entity_kind: 'campaign', entity_id: savedCampaignId },
      }));
    }
  };

  const handleLaunchWhenReady = async (campaignId: string) => {
    const expectedRevision = reviewedRevision(campaignId);
    if (expectedRevision == null) {
      setActionError('Save the campaign before arming it to launch.');
      return;
    }
    try {
      await launchWhenReady.mutateAsync({ id: campaignId, expectedRevision });
      setAudienceSyncGate(null);
      setCreativeSyncGate(null);
      setActionError(null);
      setLaunchOpen(false);
      // Arming is a terminal act for this wizard: the campaign is committed and
      // will launch itself, so there is nothing further to edit here. Leaving it
      // open behind a notice invited the operator to keep working on a campaign
      // whose next state change is not theirs to make.
      //
      // The notice is dropped rather than moved, because it was standing in for
      // feedback that now exists durably: the dashboard renders an armed pill
      // and stops calling the campaign a draft. A transient line inside a screen
      // the operator is leaving is worse than a state they can come back to.
      setView('dashboard');
    } catch (error) {
      if (isLaunchRevisionConflict(error)) {
        // A draft changed since it was reviewed is refused, not armed. Arming is offered from
        // inside the audience and creative sync gates, which are modal: the sentence would
        // land on the banner behind them. Nothing in a gate can proceed on this revision, so
        // close it and say what to do next.
        setAudienceSyncGate(null);
        setCreativeSyncGate(null);
        setActionError(LAUNCH_REVISION_CHANGED_MESSAGE);
        return;
      }
      setActionError(reportActionError(error, {
        operation: 'campaign.launch_when_ready',
        context: { entity_kind: 'campaign', entity_id: campaignId },
      }));
    }
  };

  // Deliberately no catch: a throw belongs to `CampaignLaunchDialog`, which
  // promised to stay open until the destination answers and is the only thing
  // that can keep that promise. Closing the dialog here — which is what this
  // function used to do — is what dropped the operator back into the wizard at
  // the one moment they needed the dialog most.
  const handleMarkReady = async () => {
    if (!savedCampaignId || !preflightResult?.ready || !channel) return;
    const expectedRevision = reviewedRevision(savedCampaignId);
    if (expectedRevision == null) return;
    setActionError(null);
    // A draft changed since it was reviewed is refused (409); the dialog words it.
    const readyCampaign = await launchCampaign.mutateAsync({ id: savedCampaignId, expectedRevision });
    setStatus(readyCampaign.status);
    setLaunchOpen(false);
    // What the ambient sync poll knew at the instant this launch began.
    //
    // Read from the query CACHE, not from `campaignSyncQuery`: that render
    // snapshot is as old as the last commit, and the mutation above was in
    // flight for a round trip, so the poll can have started a request since.
    // The cache changes the moment a fetch starts, which is exactly the
    // question `usePostLaunchSyncSnapshot` has to answer.
    const pollAtLaunch = queryClient.getQueryState(campaignSyncStatus.key(savedCampaignId));
    setPublishProgress({
      campaignId: savedCampaignId,
      target: deliveryTargetFor(channel),
      // Ignore any cached sync snapshot from before this launch. The first
      // fresh response can then distinguish a queued provider destination
      // from the intentional local-ready mode.
      startedAt: Date.now(),
      baselineUpdatedAt: pollAtLaunch?.dataUpdatedAt ?? 0,
      // A request already out was ISSUED before this launch, so its answer is
      // pre-launch however late it lands.
      pollInFlightAtLaunch: pollAtLaunch?.fetchStatus === 'fetching',
    });
  };

  // Closing the publish dialog after a failed launch keeps the failure: the
  // durable banner is the only thing left carrying it once the dialog is gone.
  const handleLaunchDialogDismiss = (message: string | null) => {
    setLaunchOpen(false);
    if (message) setActionError(message);
  };

  const handlePublishingDone = () => {
    if (!publishProgress || !freshCampaignSyncStatus) return;
    const outcome = campaignPublishOutcome(
      freshCampaignSyncStatus,
      publishProgress.target,
    );
    if (outcome !== 'synced' && outcome !== 'local_ready') return;
    // `live` here mirrors the D1 campaign row, which the publish promotes even
    // when the provider is holding the hierarchy paused — the pause/resume
    // actions key off that row. Whether it is actually delivering is a separate
    // fact, carried by `provider_state` and reported by the publishing modal.
    if (outcome === 'synced') setStatus('live');
    setPublishProgress(null);
    setView('dashboard');
  };

  const handlePublishingFailureDismiss = () => {
    if (!publishProgress || !freshCampaignSyncStatus) return;
    const outcome = campaignPublishOutcome(
      freshCampaignSyncStatus,
      publishProgress.target,
    );
    if (outcome !== 'failed') return;
    setActionError(
      campaignPublishingFailure(
        freshCampaignSyncStatus,
        publishProgress.target,
      ).text,
    );
    setPublishProgress(null);
  };

  const handleDuplicate = async () => {
    if (!savedCampaignId) return;
    setMoreOpen(false);
    setActionError(null);
    try {
      const duplicate = await duplicateCampaign.mutateAsync({
        id: savedCampaignId,
        newId: campaignIdForCreate(),
        name: `${name || 'Campaign'} (copy)`,
      });
      await navigate({
        to: '/campaigns/$campaignId',
        params: { campaignId: duplicate.campaign.id },
      });
    } catch (error) {
      setActionError(reportActionError(error, {
        operation: 'campaign.duplicate',
        context: { entity_kind: 'campaign', entity_id: savedCampaignId },
      }));
    }
  };

  // Extend flight (campaign lifecycle 2b, 2026-10-21). The dialog owns the request and its
  // refusals; the page takes over once it was accepted.
  const handleOpenExtend = () => {
    setMoreOpen(false);
    setActionError(null);
    setExtendOpen(true);
  };

  const handleExtended = ({ extension, plan }: { extension: CampaignFlightExtension; plan: CampaignPlan | null }) => {
    setExtendOpen(false);
    // A replayed request answers its row as it stands, which can be a failed one: say so,
    // never "extended".
    if (extension.status === 'failed') {
      setActionError(
        extension.failure
          ? `${extension.failure.headline}. ${extension.failure.next_step}`
          : 'This flight extension did not go through. Review it from the status line.',
      );
      return;
    }
    const dates = `${fmtDay(extension.start_date)} → ${fmtDay(extension.end_date)}`;
    // Committed in the route (Meta): the builder shows the new flight now. A queued one
    // (TTD) reaches the builder when its row commits (create-flow-plan-refresh).
    if (plan) applyPlanTerms(plan);
    if (extension.target === 'meta') {
      setActionNotice(
        `Flight extended: ${dates}. Meta picks the new flight up on its first day.` +
          (currentRequestedStatus === 'paused' ? ' The campaign stays paused until you resume it.' : ''),
      );
    } else {
      setActionNotice(
        `Extension queued: ${dates}. The Trade Desk ad groups pause for the few minutes it takes.`,
      );
    }
  };

  const handleRetrySync = async () => {
    if (!savedCampaignId) return;
    setMoreOpen(false);
    setActionError(null);
    try {
      await retryCampaignSync.mutateAsync(savedCampaignId);
      setActionNotice('Provider sync re-queued.');
    } catch (error) {
      setActionError(reportActionError(error, {
        operation: 'campaign.retry_sync',
        context: { entity_kind: 'campaign', entity_id: savedCampaignId },
      }));
    }
  };

  const handleArchive = async () => {
    if (!savedCampaignId || !window.confirm(`Archive “${name || 'this campaign'}”?`)) return;
    setMoreOpen(false);
    setActionError(null);
    try {
      await archiveCampaign.mutateAsync(savedCampaignId);
      setView('dashboard');
    } catch (error) {
      setActionError(reportActionError(error, {
        operation: 'campaign.archive',
        context: { entity_kind: 'campaign', entity_id: savedCampaignId },
      }));
    }
  };

  const handleDelete = async () => {
    if (!savedCampaignId || !window.confirm(`Delete draft “${name || 'this campaign'}”? This cannot be undone.`)) return;
    setMoreOpen(false);
    setActionError(null);
    try {
      await deleteCampaign.mutateAsync(savedCampaignId);
      setView('dashboard');
    } catch (error) {
      setActionError(reportActionError(error, {
        operation: 'campaign.delete',
        context: { entity_kind: 'campaign', entity_id: savedCampaignId },
      }));
    }
  };

  // The name is header metadata: renaming reaches no provider and mints no
  // revision, so a saved campaign — draft or live — is renamed in place the
  // moment the field loses focus. An unsaved draft simply carries the name
  // into its first save.
  const commitName = async () => {
    const next = name.trim();
    if (!savedCampaignId || !next || next === savedName) return;
    setActionError(null);
    try {
      await patchCampaign.mutateAsync({ id: savedCampaignId, patch: { name: next } });
      setSavedName(next);
      setActionNotice(`Renamed to “${next}”.`);
    } catch (error) {
      setActionError(reportActionError(error, {
        operation: 'campaign.rename',
        context: { entity_kind: 'campaign', entity_id: savedCampaignId },
      }));
    }
  };

  const handleDisarm = async () => {
    // Only a saved campaign can be armed, so only a saved one shows the control.
    if (!campaign) return;
    setActionError(null);
    try {
      await cancelLaunchWhenReady.mutateAsync(campaign.id);
      setActionNotice('Auto-launch disarmed. The campaign stays a draft until you launch it.');
    } catch (error) {
      // A disarm that 403s used to look exactly like one that worked, and the operator
      // would walk away from a campaign still due to launch itself.
      setActionError(reportActionError(error, {
        operation: 'campaign.disarm_auto_launch',
        context: { entity_kind: 'campaign', entity_id: campaign.id },
      }));
    }
  };

  return {
    persistStatus,
    togglePauseResume,
    undoStatus,
    handleAudienceSyncComplete,
    handleCreativeSyncComplete,
    handleLaunchWhenReady,
    handleMarkReady,
    handleLaunchDialogDismiss,
    handlePublishingDone,
    handlePublishingFailureDismiss,
    handleDuplicate,
    handleOpenExtend,
    handleExtended,
    handleRetrySync,
    handleArchive,
    handleDelete,
    commitName,
    handleDisarm,
  };
}
export type CampaignFlowActions = ReturnType<typeof campaignFlowActions>;
